import { FC, useEffect } from 'react'
import Head from 'next/head'
import Image from 'next/image'
import Link from 'next/link'
import parse from 'html-react-parser'
import { useTranslation } from 'next-i18next'
import Slider from 'react-slick'
import { useDispatch, useSelector } from 'react-redux'
import { showSimulation } from 'store/simulation/actions/showSimulation'
import { Store } from 'store/types'
import Button from 'components/button/Button'
import { useRouter } from 'next/router'
import { Language } from 'types/language'
import NewsArticles from 'components/news-articles/NewsArticles'
import NewsArticle from 'components/news-articles/NewsArticle'
import { Choose, Earth, HouseSmall, House, Plug, Step2 } from './assets'
import { routes } from 'utils/routes'
import ProducerCard from 'components/producers/producer-card/ProducerCard'
import classNames from 'classnames'
import { trackEvent } from 'utils/eventTracking'
import { PhoneAvailability } from 'components/phone-availibility/PhoneAvailability'
import ProjectCard from 'components/projects/ProjectCard'
import { HomeProps as Props } from './types'
import styles from './Home.module.scss'
import buttonStyles from 'components/button/Button.module.scss'
import Testimonials from './components/Testimonials/Testimonials'
import { getHeroImage } from 'utils/helpers'
import LookingForHelp from 'components/looking-for-help/LookingForHelp'
import { TITLE_POSTFIX } from 'constants/seo'
import { SimulationType } from 'store/marketing/types'
import EncyclopediaNavigationMenu from 'components/encyclopedia/navigation-menu/EncyclopediaNavigationMenu'

const Home: FC<Props> = ({ categories, producers, projects, heroImage }) => {
  // REDUX STORE
  const { simulationType } = useSelector((store: Store) => store.marketing)
  const { didSimulation } = useSelector((store: Store) => store.simulation)
  const dispatch = useDispatch()

  // Next router
  const router = useRouter()
  const { locale } = router

  // i18n
  const { t } = useTranslation(['home', 'lookingForHelp'])

  /**
   * Triggered on first render
   */
  useEffect(() => {
    // Add is-home class to body
    document.body.classList.add('is-home')

    return () => {
      document.body.classList.remove('is-home')
    }
  }, [])

  /**
   * Handles the click event on the CTA buttons
   */
  const handleCtaClick = () => {
    if (didSimulation) {
      router.push(routes(locale).producers)
    } else {
      dispatch(showSimulation())

      // Track 'Simulation Started' event @Semetis
      trackEvent('simulation_started')
    }
  }

  return (
    <>
      {/* SEO */}
      <Head>
        <title>
          {t('seoTitle')} {TITLE_POSTFIX}
        </title>
        <meta name="description" content={t('seoDescription')} />
        <meta property="og:title" content={`${t('seoTitle')} ${TITLE_POSTFIX}`} key="title" />
        <meta property="og:description" content={t('seoDescription')} />
      </Head>

      <main className={styles.main}>
        {/* HERO SECTION */}
        <section className={styles.hero}>
          <div className="container has-bg-image content-is-aligned-vertically">
            <div>
              {/* TITLE */}
              <h1 className={styles.title}>{parse(t('slogan', 'Wij geven een zak om eerlijke energie'))}</h1>

              {/* CTA */}
              <div className="margin-y-l">
                <Button onClick={() => handleCtaClick()} withArrow>
                  {didSimulation || simulationType !== SimulationType.PRICING_SIMULATION
                    ? t('pickYourProducer', 'Kies jouw producent')
                    : t('simulateYourPrice', 'Bereken jouw prijs')}
                </Button>
              </div>

              {/* HELP NEEDED */}
              <div>
                <LookingForHelp isHome />
              </div>
            </div>
          </div>
          <div className={styles.heroImageContainer}>
            <Image priority src={getHeroImage(heroImage)} layout="fill" objectFit="cover" alt="Bolt Energie" placeholder="blur" />
          </div>
        </section>

        {/* WHAT IS BOLT? */}
        <section className="section is-dark">
          <div className="container">
            <div className="columns is-vertically-aligned">
              <div className="column is-half margin-bottom-none">
                <div className="video-16-9">
                  {locale === Language.DUTCH && (
                    <iframe
                      title="Koop echte energie van echte mensen"
                      src="https://player.vimeo.com/video/364788995?autoplay=1&muted=1"
                      frameBorder="0"
                      allowFullScreen
                      allow="autoplay"
                    />
                  )}

                  {locale === Language.FRENCH && (
                    <iframe
                      title="Une énergie vraiment durable par de vrais producteurs"
                      src="https://player.vimeo.com/video/681420808?autoplay=1&muted=1"
                      frameBorder="0"
                      allowFullScreen
                      allow="autoplay"
                    />
                  )}
                </div>
              </div>

              <div className="column is-half intro">
                <h2>{t('whatIsBolt', 'Wat is Bolt?')}</h2>
                <p>
                  {t(
                    'whatIsBoltExplained',
                    'Bolt is een energieleverancier en laat je helemaal zelf kiezen waar je energie vandaan komt. Is het nu via windmolens, zonnepanelen of biogas.'
                  )}
                </p>
              </div>
            </div>
          </div>
        </section>

        {/* TESTIMONIALS */}
        <section className="section is-bright">
          <Testimonials />
        </section>

        {/* PRODUCERS SHOWCASE */}
        <section className="section is-dark">
          <House viewBox="0 0 701 693" className={classNames(styles.house, styles.right)} />
          <HouseSmall viewBox="0 0 354 353" className={classNames(styles.house, styles.left)} />

          <div className="container">
            <h2>{t('featuredProducers', 'Producenten in de schijnwerpers')}</h2>

            <p className={classNames(styles.subtitle, 'is-large', styles.readable)}>
              {t('featuredProducersSubtitle', 'Ontdek hoeveel jij kan besparen bij jouw favoriete producent.')}
            </p>

            <div className={styles.producers}>
              {producers.map((producer, idx) => (
                <div key={producer.id}>
                  <ProducerCard key={`producer-${producer.slug}-${idx}`} producer={producer} showCTA />
                </div>
              ))}
            </div>

            <Button onClick={() => router.push(routes(locale).producers)} isFullWidthMobile isInverse withArrow>
              {t('featuredProducersCTA', 'Bekijk al onze producenten')}
            </Button>
          </div>
        </section>

        {/* ENCYCLOPEDIA */}
        {locale === Language.DUTCH && (
          <section className="section is-bright">
            <EncyclopediaNavigationMenu categories={categories} />
          </section>
        )}

        {/* HOW IT WORKS */}
        <section id="how-it-works" className="section is-dark">
          <div className="container">
            <h2>{t('howItWorks', 'Hoe werkt het?')}</h2>

            <ul className={styles.steps}>
              <li>
                <div className={classNames('card', styles.card)}>
                  <div className={styles.img}>
                    <Choose />
                  </div>
                  <h4 className={styles.title}>{t('step1Title', 'Kies je producent')}</h4>
                  <p>{t('step1Text', 'Kies voor je favoriete lokale bron van groene energie.')}</p>
                </div>
              </li>

              <li>
                <div className={classNames('card', styles.card)}>
                  <div className={styles.img}>
                    <Step2 />
                  </div>
                  <h4 className={styles.title}>{t('step2Title', 'Registreer jezelf')}</h4>
                  <p>{t('step2Text', 'Registreer je op de wachtlijst van jouw producent.')}</p>
                </div>
              </li>

              <li>
                <div className={classNames('card', styles.card)}>
                  <div className={styles.img}>
                    <Plug />
                  </div>
                  <h4 className={styles.title}>{t('step3Title', 'Bolt doet de rest')}</h4>
                  <p>
                    {t('step3Text', 'Bolt neemt na onze start in deze lente contact met jou op en neemt automatisch je contract over.')}
                  </p>
                </div>
              </li>

              <li>
                <div className={classNames('card', styles.card)}>
                  <div className={styles.img}>
                    <Earth />
                  </div>
                  <h4 className={styles.title}>{t('step4Title', 'Trotse Bolt gebruiker')}</h4>
                  <p>{t('step4Text', 'Jij geniet van 100% lokale energie en helpt België duurzamer te maken.')}</p>
                </div>
              </li>
            </ul>

            <Button onClick={() => handleCtaClick()} isFullWidthMobile isInverse withArrow withGradient>
              {didSimulation || simulationType !== SimulationType.PRICING_SIMULATION
                ? t('pickYourProducer', 'Kies jouw producent')
                : t('simulateYourPrice', 'Bereken jouw prijs')}
            </Button>
          </div>
        </section>

        {/* NEWS ARTICLES */}
        <section className="section">
          <Slider
            infinite
            slidesToShow={3}
            slidesToScroll={1}
            initialSlide={1}
            centerMode
            responsive={[
              {
                breakpoint: 920,
                settings: {
                  slidesToShow: 2
                }
              },
              {
                breakpoint: 760,
                settings: {
                  slidesToShow: 1
                }
              }
            ]}
            dots
            dotsClass="slick-dots"
            // eslint-disable-next-line jsx-a11y/anchor-has-content
            customPaging={() => <a />}
          >
            {NewsArticles[locale].map((newsArticle) => (
              <NewsArticle key={newsArticle.id} newsArticle={newsArticle} className="padding-s" withBorder />
            ))}
          </Slider>
        </section>

        {/* I AM PRODUCER */}
        <section id="producer" className={classNames('section', 'is-green', styles['center-from-tablet'], styles['i-am-producer'])}>
          <div className="container">
            <h2>{t('areYouProducerTitle', 'Wil jij energie-opwekker worden bij Bolt?')}</h2>

            <p className={classNames(styles.readable, styles['is-auto-center'], styles.info)}>
              {t(
                'areYouProducerSubtitle',
                'Fantastisch! Door je duurzame energie én garanties van oorsprong (gvo’s) via Bolt te verkopen, weet je zeker dat je stroom lokaal afgenomen wordt. Om energie-opwekker te worden heb je een industriële installatie nodig om zonne-, wind-, water- of bio-energie mee op te wekken. De installatie moet een vermogen van minstens 10 kva hebben, en een apart EAN-nummer voor injectie.'
              )}
            </p>

            <Link href={routes(locale).generator}>
              <a
                className={classNames(
                  buttonStyles.btn,
                  buttonStyles['btn-primary'],
                  buttonStyles['is-inverse'],
                  buttonStyles['with-gradient'],
                  buttonStyles['is-fullwidth-mobile'],
                  'margin-bottom-m',
                  'is-inline-block'
                )}
              >
                {t('areYouProducerCTA', 'Vul het formulier in')}
              </a>
            </Link>

            <p className="margin-top-l margin-bottom-s">
              {t('lookingForHelp:questionsAboutBolt', 'Vragen over Bolt?')}
              &nbsp;
              <a href="tel:028993300" className={styles['phone-link']}>
                {t('lookingForHelp:callNumber', 'Bel 02 899 33 00')}
              </a>
            </p>

            <PhoneAvailability />
          </div>
        </section>

        {/* PROJECTS */}
        <section className={classNames('section', styles.projects)}>
          <div className="container">
            <h2>{t('projectsTitle', 'Bekijk onze projecten')}</h2>
            <div className="columns">
              {projects.map((project) => {
                const { id } = project

                return (
                  <div key={id} className="column is-one-third">
                    <ProjectCard project={project} />
                  </div>
                )
              })}
            </div>
            <Button onClick={() => router.push(routes(locale).projects)} isFullWidthMobile isInverse withArrow>
              {t('projectsCTA', 'Bekijk al onze projecten')}
            </Button>
          </div>
        </section>
      </main>
    </>
  )
}

export default Home
